// ============================================================
// BOOTH — the links the assistant hands out.
//
// Two jobs, both done here rather than asked of the model, because a rule in
// a prompt is followed most of the time and a function is followed all of it.
//
//  1. A bare address becomes a link. The assistant writes "go to
//     boothapp.co/auth/signup" in the middle of a sentence, which is how a
//     person writes it — and markdown leaves that as plain text, so the one
//     instruction in the whole answer was the one thing you could not click.
//     GFM autolinks only what carries a scheme, and nobody types https:// in
//     a sentence.
//
//  2. Every link carries where it came from. The app already turns utm_* in
//     a URL into a 90-day attribution cookie (lib/attribution.ts), which the
//     signup and the Meta events read — so tagging the assistant's links
//     makes a shop that listed after talking to it attributable, with no new
//     plumbing at either end.
// ============================================================

const HOSTS = ["boothapp.co", "www.boothapp.co"]

/** Where a click came from. utm_medium separates it from any campaign that
 *  might also use a booth-ish utm_source. */
export const CHAT_SOURCE = "booth-chat"
export const CHAT_MEDIUM = "assistant"

/** Is this one of ours? A relative path is, by definition. */
export function isBoothLink(href: string | undefined): boolean {
  if (!href) return false
  if (href.startsWith("/") || href.startsWith("#")) return true
  try {
    const url = new URL(href)
    if (url.protocol !== "https:" && url.protocol !== "http:") return false
    return HOSTS.includes(url.hostname) || url.hostname === "localhost"
  } catch {
    return false
  }
}

/**
 * The same link, tagged as coming from the assistant.
 *
 * Only our own links, and never over a tag that is already there — the first
 * source wins everywhere else in the app too.
 */
export function withChatAttribution(href: string): string {
  if (!isBoothLink(href)) return href
  try {
    const url = new URL(href, "https://www.boothapp.co")
    if (url.searchParams.has("utm_source")) return href
    url.searchParams.set("utm_source", CHAT_SOURCE)
    url.searchParams.set("utm_medium", CHAT_MEDIUM)
    return href.startsWith("/") ? url.pathname + url.search + url.hash : url.toString()
  } catch {
    return href
  }
}

// A BOOTH address written the way a person writes one: no scheme, maybe a
// path, usually with the sentence carrying on afterwards. The final character
// class excludes sentence punctuation, so "boothapp.co/help." links the
// address and leaves the full stop behind.
const BARE = /(?<![\w/@.[("])((?:https?:\/\/)?(?:www\.)?boothapp\.co(?:\/[\w\-./#?=&%+]*[\w\-/#=&%+])?)/g

/**
 * Wrap bare BOOTH addresses in markdown links, leaving anything already
 * linked alone.
 *
 * Existing links are cut out first and put back afterwards, which is easier
 * to be sure of than a regex clever enough to know what it is standing
 * inside.
 */
export function autolinkBooth(text: string): string {
  const kept: string[] = []
  const frozen = text.replace(/\[[^\]]*\]\([^)]*\)|<[^>\s]+>/g, (match) => {
    kept.push(match)
    return " " + (kept.length - 1) + " "
  })

  const linked = frozen.replace(BARE, (match) => {
    const href = match.startsWith("http") ? match : "https://" + match.replace(/^www\./, "")
    return "[" + match + "](" + href + ")"
  })

  return linked.replace(/ (\d+) /g, (_, i) => kept[Number(i)])
}
